import React from 'react'
import { useAppDispatch } from '../../hooks/redux'

import {
  postUser,
  postUserNote,
  putUser,
  uploadFileAvatar
} from '../../lib/api'
import { useUserAPI } from '../UserAPIProvider'
import { useMetaContext } from '../MetaProvider'
import { useEnvironment } from '../EnvironmentProvider'
import { useEditContext } from '../EditProvider'

import useObjectChanged from '../../hooks/useObjectChanged'
import { AddErrorAlert, AddSuccessAlert } from '../../slices/alert'
import { updateChannel } from '../../slices/user'

export const CustomerContext = React.createContext(null)

const countries = [
  { id: 1, country: 'USA' },
  { id: 2, country: 'Canada' }
]

const emptyVals = {
  name: '',
  workPhone: '',
  email: '',
  tags: [],
  type: '',
  middleName: '',
  timezone: ''
}

const SUCCESS_DURATION = 2500
const ON_FILES_STORAGE_KEY = 'ON_FILES'

// User is only needed if this provider is used for edting
const CustomerProvider = ({ user, children }) => {
  const dispatch = useAppDispatch()
  const { updateUserMeta, updateUser } = useUserAPI()
  const { setMeta, metaCache } = useMetaContext()
  const { user: envUser, update, getEnvValue } = useEnvironment()
  const { setInitial, set, setAfterSet, isSet, isAuthSet, setAuth, abc } =
    useObjectChanged()
  const { editedMeta, setEditedAuth, setEditedMeta } = useEditContext()

  const [values, setValues] = React.useState(emptyVals)
  const [notes, setNotesToProvider] = React.useState([])
  const [channels, setChannelsToProvider] = React.useState([])
  const [saved, setSaved] = React.useState(null)
  const [avatarFile, _setAvatarFile] = React.useState(null)
  const [old, setOld] = React.useState(null)
  const [changed, setChanged] = React.useState(false)
  const [updatedNotes, _setUpdatedNotes] = React.useState([])
  const [customerSession, setCustomerSession] = React.useState(null)
  const [savedMessage, setSavedMessage] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [onFiles, setOnFiles] = React.useState(
    (localStorage.getItem(ON_FILES_STORAGE_KEY) ?? 'false') === 'true'
  )

  React.useEffect(() => {
    localStorage.setItem(ON_FILES_STORAGE_KEY, onFiles.toString())
  }, [onFiles])

  const setAvatarFile = React.useCallback(file => {
    _setAvatarFile(file)
    setChanged(true)
  }, [])

  const handleChange = (input, prop, i) => {
    if (input !== -1) {
      if (prop === 'tags') {
        input = [...values.tags, input]
      }
    } else {
      // slice off tags
      const oldTags = [...values.tags]
      oldTags.splice(i, 1)
      input = [...oldTags]
    }
    setValues(vals => {
      return { ...vals, [prop]: input }
    })

    if (input) {
      setChanged(true)
    }
  }

  React.useEffect(() => {
    if (values && !set) {
      setInitial(values)
    } else {
      setAfterSet(values)
    }
  }, [values, set, setInitial, setAfterSet, abc])

  const handleSavedTags = React.useCallback(input => {
    setValues(vals => ({ ...vals, tags: input }))
  }, [])

  // Send user to server
  const handleUploadUser = React.useCallback(async () => {
    const user = {
      name: values.name,
      role_id: null,
      channels: [],
      media_ids: [],
      avatar_id: null,
      tags: values.tags,
      status_id: values.status ? values.status : null,
      // default as customer for now if null or else api will throw error
      type_id:
        values.type === ''
          ? getEnvValue('config.users', 'u_default_external_type_id', null) ??
            getEnvValue('config.users', 'users_default_external_type_id', null)
          : values.type,
      timezone: values.timezone === '' ? null : values.timezone
    }

    try {
      // if avatar is uploaded, update user
      if (avatarFile) {
        const imageId = await uploadFileAvatar(avatarFile)
        user.avatar_id = imageId.id
      }

      // creates a new user
      const newUser = await postUser(user)

      // adding 'Know Your Client' notes to user after creation
      notes.forEach(note => {
        postUserNote(newUser.id, note).catch(err => {
          console.error('Error posting notes', err)
        })
      })

      // updating users in provider
      updateUser(newUser)

      await Promise.all(
        channels.map(
          ({ id, dirty, ...channel }) =>
            new Promise((res, reject) => {
              if (dirty) {
                dispatch(updateChannel(newUser.id, channel, id, res, reject))
              }
            })
        )
      )

      // // clears the state on save
      setSaved(true)
      setValues(emptyVals)
      setChanged(false)

      // Set success message and clear after a delay
      dispatch(AddSuccessAlert('User created.', SUCCESS_DURATION * 1.1))
      setSavedMessage(true)
      return newUser
    } catch (err) {
      //if error comes from channel, it is empty so dont display.
      if (err) {
        console.error('Error while creating user', err)
        dispatch(AddErrorAlert({ err }))
      }
    }
  }, [values, notes, channels, updateUser, avatarFile, getEnvValue, dispatch])

  const handleUpdateUser = React.useCallback(
    async (_, alwaysSaveMeta) => {
      if (!user) {
        return
      }

      setIsSaving(true)

      const {
        id,
        name,
        role_id,
        avatar_id,
        status_id,
        type_id,
        //tags,
        timezone,
        media_ids
      } = user

      let uploadId
      if (avatar_id?.name) {
        try {
          const imageId = await uploadFileAvatar(avatar_id)
          uploadId = imageId?.id
        } catch (err) {
          console.error('Error uploading avatar', err)
        }
      } else {
        // if avatart exists - uuid
        uploadId = avatar_id
      }

      // Don't save channels here, those are done separately
      const newUser = {
        id,
        name: values.name === '' ? name : values.name,
        role_id: role_id || null,
        avatar_id: uploadId || null,
        status_id: status_id || null,
        type_id: type_id || null,
        tags: values.tags ?? [],
        timezone: timezone || null,
        media_ids: media_ids || []
      }

      if (user.auth) {
        // guard case when auth email is given but activity switch isn't checked
        if (!Object.keys(user.auth).includes('suspended_at')) {
          newUser.auth = {
            email: user.auth.email ?? null,
            suspended_at: new Date().toISOString()
          }
        } else {
          newUser.auth = {
            email: user.auth.email ?? null,
            suspended_at: user.auth.suspended_at ?? null
          }
        }
      }

      try {
        // update environment if self
        if (id === envUser?.id) {
          update(newUser)
        }

        // only call api if meta has been updated or its editing inline mode
        if (editedMeta || alwaysSaveMeta) {
          const newMeta = await updateUserMeta(id, values, old, newUser)
          setMeta(newMeta)
          if (id in metaCache.current) {
            metaCache.current[id] = newMeta
          }
          isSet(false)
        }

        // update email first
        if (newUser.auth?.email?.length) {
          // resetting auth object to enable/disable save button
          const updated = await putUser(newUser)
          //update environment itself with channels with full data
          if (id === envUser?.id) {
            update(updated)
          }
          updateUser(updated)
          setAuth(user.auth)
          isAuthSet(false)
        } else {
          const updated = await putUser(newUser)
          updateUser(updated)
        }

        setChanged(false)
        setEditedAuth(false)
        setEditedMeta(false)
        setSavedMessage(true)
        setIsSaving(false)
        return true
      } catch (err) {
        console.error('Error while updating user', err)
        dispatch(AddErrorAlert({ err }))
        setIsSaving(false)
        return false
      }
    },
    [
      user,
      updateUserMeta,
      values,
      old,
      setMeta,
      updateUser,
      envUser,
      update,
      editedMeta,
      isSet,
      setEditedAuth,
      isAuthSet,
      setAuth,
      setEditedMeta,
      metaCache,
      dispatch
    ]
  )

  const handleUpdateUserOnBlur = async selectedUser => {
    if (!selectedUser) {
      return
    }
    const {
      id,
      name,
      role_id,
      avatar_id,
      status_id,
      type_id,
      tags,
      channels: currentChannels,
      media_ids
    } = selectedUser

    let uploadId
    if (avatar_id?.name) {
      try {
        const imageId = await uploadFileAvatar(avatar_id)
        uploadId = imageId?.id
      } catch (err) {
        console.error('Error uploading avatar in update', err)
      }
    } else {
      // if avatart exists - uuid
      uploadId = avatar_id
    }

    // Get old channels, if any
    let newChannels = [...channels]

    // Merge old channels with the ones from this user
    currentChannels.forEach(ch => {
      const oldChannelIdx = newChannels.findIndex(nch => nch.id === ch.id)

      if (oldChannelIdx >= 0) {
        const oldCh = newChannels[oldChannelIdx]
        newChannels[oldChannelIdx] = { ...oldCh, ...ch }
      } else {
        newChannels.push(ch)
      }
    })

    // Filter out any dis-allowed keys
    newChannels = newChannels.map(ch => ({
      id: ch.id,
      type: ch.type,
      name: ch.name,
      value: ch.value
    }))

    const newUser = {
      id,
      name: values.name === '' ? name : values.name,
      channels: newChannels,
      role_id: role_id || null,
      avatar_id: uploadId || null,
      status_id: status_id || null,
      type_id: type_id || null,
      tags: tags ?? [],
      media_ids: media_ids || []
    }

    if (selectedUser.auth) {
      // guard case when auth email is given but activity switch isn't checked
      if (!Object.keys(selectedUser.auth).includes('suspended_at')) {
        newUser.auth = {
          email: selectedUser.auth.email ?? null,
          suspended_at: new Date().toISOString()
        }
      } else {
        newUser.auth = {
          email: selectedUser.auth.email ?? null,
          suspended_at: selectedUser.auth.suspended_at ?? null
        }
      }
    }

    try {
      // update environment if self
      if (id === envUser?.id) {
        update(newUser)
      }

      const newMeta = await updateUserMeta(id, values, old, newUser)
      setMeta(newMeta)
      if (id in metaCache.current) {
        metaCache.current[id] = newMeta
      }
      isSet(false)

      // update email first
      if (newUser.auth?.email?.length) {
        // resetting auth object to enable/disable save button
        const updated = await putUser(newUser)
        updateUser(updated)
        setAuth(selectedUser.auth)
        isAuthSet(false)
      } else {
        const updated = await putUser(newUser)
        updateUser(updated)
      }

      setChanged(false)
      setEditedAuth(false)
      setEditedMeta(false)
      // Set success message and clear after a delay
      dispatch(AddSuccessAlert('Changes saved.', SUCCESS_DURATION * 1.1))
    } catch (err) {
      console.error('Error while updating user', err)
      dispatch(AddErrorAlert({ err }))
      return false
    }
    return true
  }

  const notesToProvider = React.useCallback(data => {
    setNotesToProvider(data)
    if (data.length) {
      setChanged(true)
    }
  }, [])

  const channelsToProvider = React.useCallback(data => {
    setChannelsToProvider(data)
    if (data.length) {
      setChanged(true)
    }
  }, [])

  const clearChanged = React.useCallback(() => {
    setChanged(false)
  }, [])

  return (
    <>
      <CustomerContext.Provider
        value={{
          values,
          setValues,
          handleChange,
          countries,
          handleUploadUser,
          handleUpdateUser,
          handleUpdateUserOnBlur,
          setNotesToProvider,
          notesToProvider,
          channelsToProvider,
          saved,
          setSaved,
          setAvatarFile,
          emptyVals,
          setOld,
          handleSavedTags,
          changed,
          clearChanged,
          updatedNotes,
          setCustomerSession,
          customerSession,
          _setUpdatedNotes,
          resetVals: () => setValues(emptyVals),
          isSaving,
          setIsSaving,
          savedMessage,
          setSavedMessage,
          onFiles,
          setOnFiles
        }}
      >
        {children}
      </CustomerContext.Provider>
    </>
  )
}

export default CustomerProvider

export const useCustomerContext = () => React.useContext(CustomerContext)
